import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { PersonalFormValuesProps } from './types';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useProNavigate } from '@mp/contexts';
import { useToast } from '@medely/ui-kit';

export const usePersonalFormSubmit = () => {
  const { axios } = useNetworkRequest();
  const { navigate } = useProNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate: onSubmitAccount, isLoading: submitLoading } = useMutation(
    (values: PersonalFormValuesProps) =>
      axios.put(`/v3/professional/accounts`, {
        account: {
          address_first: values.address_first,
          address_second: values.address_second,
          address_zip: values.address_zip,
          address_city: values.address_city,
          state_id: values.state_id,
          phone_number: values.phone_number,
        },
        professional: { nickname: values.nickname },
      }),
    {
      onSuccess: () => {
        toast.success('Profile updated');
        navigate('Profile');
        queryClient.invalidateQueries(['GetCurrentUserForPersonalForm']);
      },
      onError: (e: AxiosError<{ message?: string }>) => {
        toast.error(e?.response?.data?.message ?? 'Error while updating profile');
      },
    },
  );

  const { mutate: createEmergencyContact, isLoading: createEmergencyLoading } = useMutation(
    (values: PersonalFormValuesProps) =>
      axios.post(`/v3/professional/emergency_contacts`, {
        first_name: values.emergency_first_name,
        last_name: values.emergency_last_name,
        phone_number: values.emergency_phone_number,
      }),
    {
      onSuccess: () => {
        navigate('Profile');
        queryClient.invalidateQueries(['GetEmergencyContactForPersonalForm']);
      },
    },
  );

  const { mutate: updateEmergencyContact, isLoading: updateEmergencyLoading } = useMutation(
    (values: PersonalFormValuesProps) =>
      axios.put(`/v3/professional/emergency_contacts/${values.emergency_id}`, {
        first_name: values.emergency_first_name,
        last_name: values.emergency_last_name,
        phone_number: values.emergency_phone_number,
      }),
    {
      onSuccess: () => {
        navigate('Profile');
        queryClient.invalidateQueries(['GetEmergencyContactForPersonalForm']);
      },
    },
  );

  const isSubmitting = submitLoading || createEmergencyLoading || updateEmergencyLoading;

  return {
    updateEmergencyContact,
    createEmergencyContact,
    onSubmitAccount,
    isSubmitting,
  };
};
